import type { Workspace, WorkspaceRole } from "./workspaces.types";

/**
 * Mock workspace data for Phase 1 UI development.
 *
 * These fixtures are for workspace-detail and card component tests. They must
 * not be used to drive an unscoped workspace-list API request.
 *
 * The mock returns the UI shape (camelCase) directly — there's no value
 * round-tripping through the snake_case `WorkspaceApi` shape just to map back.
 */

/**
 * Mock role for the "currently signed-in" user. Flip to `"viewer"` or
 * `"editor"` to verify the "All workspaces" tab disappears for non-owners.
 */
export const MOCK_CURRENT_USER_ROLE: WorkspaceRole = "owner";

/** Stable fixture using real workspace IDs — rich data for full UI testing. */
export const MOCK_WORKSPACES: Workspace[] = [
  {
    id: "mock-workspace-uuid-001",
    tenantId: "mock-tenant-uuid-001",
    name: "Mock Workspace",
    description: "Mock workspace for testing story build page.",
    isActive: true,
    createdAt: "2026-06-15T05:18:40.727229Z",
    updatedAt: "2026-06-15T05:18:40.727229Z",
    owner: {
      id: "mock-user-uuid-001",
      name: "Mock User",
      email: "mock@example.com",
    },
    members: [
      { id: "u-mock-1", name: "Alice Chen", avatarUrl: null },
      { id: "u-mock-2", name: "Barry Diaz", avatarUrl: null },
    ],
    membersTotal: 2,
    dataSourceCount: 1,
    dataStoryCount: 1,
    currentUserRole: "owner",
  },
  {
    id: "059e7b0b-b65e-4ea6-8bf3-346cfc09b52c",
    tenantId: "66b93c85-0e92-4b39-ab63-2965eb1cfdad",
    name: "Marketing ops",
    description: "Campaign attribution, channel ROI and lead intelligence.",
    isActive: true,
    createdAt: "2026-06-15T05:18:40.727229Z",
    updatedAt: "2026-06-15T05:18:40.727229Z",
    owner: {
      id: "f56de888-cfc6-4e0f-8e8a-31e5b0b0b3c0",
      name: "Udhaya Prakash",
      email: "udhayaprakash122@gmail.com",
    },
    members: [
      { id: "u-1", name: "Alice Chen", avatarUrl: null },
      { id: "u-2", name: "Barry Diaz", avatarUrl: null },
      { id: "u-3", name: "Carla Wu", avatarUrl: null },
      { id: "u-4", name: "Dev Patel", avatarUrl: null },
    ],
    membersTotal: 6,
    dataSourceCount: 4,
    dataStoryCount: 5,
    currentUserRole: "owner",
  },
  {
    id: "a3ae1626-6363-4208-b5d3-cf8297976279",
    tenantId: "66b93c85-0e92-4b39-ab63-2965eb1cfdad",
    name: "Product analytics",
    description: "Funnel metrics, retention cohorts and feature adoption.",
    isActive: true,
    createdAt: "2026-06-15T05:00:12.992234Z",
    updatedAt: "2026-06-15T05:00:12.992234Z",
    owner: {
      id: "f56de888-cfc6-4e0f-8e8a-31e5b0b0b3c0",
      name: "Udhaya Prakash",
      email: "udhayaprakash122@gmail.com",
    },
    members: [
      { id: "u-5", name: "Eve Park", avatarUrl: null },
      { id: "u-6", name: "Faisal Khan", avatarUrl: null },
      { id: "u-7", name: "Grace Lin", avatarUrl: null },
    ],
    membersTotal: 5,
    dataSourceCount: 6,
    dataStoryCount: 3,
    currentUserRole: "owner",
  },
  {
    id: "5fc00260-31f2-4cbe-8323-dd33f98b1162",
    tenantId: "66b93c85-0e92-4b39-ab63-2965eb1cfdad",
    name: "Finance and controls",
    description: "Spend governance, anomaly detection and board reporting.",
    isActive: true,
    createdAt: "2026-06-12T12:14:33.142114Z",
    updatedAt: "2026-06-12T12:14:33.142114Z",
    owner: {
      id: "f56de888-cfc6-4e0f-8e8a-31e5b0b0b3c0",
      name: "Udhaya Prakash",
      email: "udhayaprakash122@gmail.com",
    },
    members: [
      { id: "u-8", name: "Hugo Schwartz", avatarUrl: null },
      { id: "u-9", name: "Isla Murphy", avatarUrl: null },
      { id: "u-10", name: "Jorge Ortiz", avatarUrl: null },
      { id: "u-11", name: "Kavya Reddy", avatarUrl: null },
    ],
    membersTotal: 8,
    dataSourceCount: 3,
    dataStoryCount: 7,
    currentUserRole: "editor",
  },
  {
    id: "3e956b99-c844-402a-88e7-141213e7e6f2",
    tenantId: "66b93c85-0e92-4b39-ab63-2965eb1cfdad",
    name: "People insights",
    description: "Headcount planning, attrition models and DEI metrics.",
    isActive: true,
    createdAt: "2026-06-10T02:36:28.529429Z",
    updatedAt: "2026-06-10T02:36:28.529429Z",
    owner: {
      id: "f56de888-cfc6-4e0f-8e8a-31e5b0b0b3c0",
      name: "Udhaya Prakash",
      email: "udhayaprakash122@gmail.com",
    },
    members: [
      { id: "u-12", name: "Liam Wright", avatarUrl: null },
      { id: "u-13", name: "Mia Torres", avatarUrl: null },
    ],
    membersTotal: 3,
    dataSourceCount: 2,
    dataStoryCount: 4,
    currentUserRole: "owner",
  },
  // Default workspace — auto-created, user has no membership role
  {
    id: "f6cbc195-0aab-4cad-b7b9-6abf20e18b18",
    tenantId: "66b93c85-0e92-4b39-ab63-2965eb1cfdad",
    name: "Default",
    description: "Auto-created default workspace.",
    isActive: true,
    createdAt: "2026-06-05T06:57:34.705075Z",
    updatedAt: "2026-06-05T06:57:34.705075Z",
    owner: null,
    members: [],
    membersTotal: 0,
    dataSourceCount: 4,
    dataStoryCount: 0,
    currentUserRole: "viewer",
  },
];

/**
 * Helper used by the hook — returns true when the signed-in user is a member of
 * (or owns) the workspace, i.e. it belongs in the "My workspaces" tab.
 *
 * For Phase 1 we treat any non-viewer role as a membership signal.
 * The real API will return this directly via `current_user_role` once shipped.
 */
export const isMyWorkspace = (w: Workspace): boolean =>
  w.currentUserRole === "owner" || w.currentUserRole === "editor";
